import type { AnimalQuery, AnimalRead, PersonRead } from '@pedigreesmart/api-client'
import { contenutoDenuncia, iri, typeLabel, type DeclarationType } from '@pedigreesmart/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type SyntheticEvent } from 'react'

import { useSession, useTerm } from '../session/SessionProvider.js'
import '../styles/forms.css'
import './declarations.css'

/** I tipi che il gestionale sa comporre, nell'ordine in cui si usano. */
const TIPI: readonly DeclarationType[] = ['registration', 'mating', 'birth', 'transfer', 'other']

interface Nato {
  name: string
  sex: 'M' | 'F' | 'U'
  color: string
}

/**
 * L'apertura di una denuncia.
 *
 * La denuncia è il documento con cui si chiede all'associazione di scrivere
 * nel registro, e il gestionale finora sapeva farla avanzare ma non aprirla:
 * il percorso che crea i soggetti con i controlli si poteva iniziare solo
 * fuori dall'applicazione.
 *
 * **Ogni tipo ha un modulo diverso**, perché ha un contenuto diverso: una
 * monta vuole fattrice, stallone e data; una nascita vuole la data e l'elenco
 * dei nati; un passaggio vuole il soggetto e il nuovo proprietario. Sono i
 * campi che il server legge davvero dal contenuto della denuncia — non un
 * elenco inventato qui — e sono anche quelli che decidono se la registrazione
 * potrà andare a buon fine.
 *
 * **La denuncia nasce sempre in bozza.** Non c'è un pulsante «apri e invia»:
 * il contenuto è modificabile finché è in bozza e si congela alla
 * presentazione, quindi conviene poterlo rileggere prima. La presentazione si
 * fa dall'elenco, dove ci sono già tutte le transizioni.
 *
 * **Niente controlli duplicati.** Le regole vere — la fattrice dev'essere
 * femmina, lo stallone maschio, le due specie devono coincidere — le applica
 * il server, e l'anteprima della registrazione le mostra tutte insieme prima
 * dell'unica azione irreversibile. Ripeterle qui vorrebbe dire tenerle
 * allineate in due posti.
 */
export function DeclarationForm({
  onFatto,
  onAnnulla,
}: {
  onFatto: () => void
  onAnnulla: () => void
}) {
  const { resources, context, species } = useSession()
  const cache = useQueryClient()
  const parolaNato = useTerm('animal.young')
  // Le parole si leggono **sempre**, in cima e non dentro un ramo del JSX:
  // `useTerm` è un hook, e chiamarlo solo per certi tipi di denuncia
  // cambierebbe l'ordine degli hook fra un render e l'altro — React si ferma
  // con «Rendered fewer hooks than expected» al primo cambio di tipo.
  const parolaFattrice = useTerm('dam')

  const [tipo, setTipo] = useState<DeclarationType>('registration')
  const [errore, setErrore] = useState<string | null>(null)
  const [violazioni, setViolazioni] = useState<Record<string, string>>({})

  const [denunciante, setDenunciante] = useState<PersonRead | null>(null)
  const [proprietario, setProprietario] = useState<PersonRead | null>(null)
  const [soggetto, setSoggetto] = useState<AnimalRead | null>(null)
  const [stallone, setStallone] = useState<AnimalRead | null>(null)
  const [nati, setNati] = useState<readonly Nato[]>([{ name: '', sex: 'U', color: '' }])

  const codiceSpecie = species?.code ?? context?.species[0]?.code ?? ''

  const razze = useQuery({
    queryKey: ['breeds', codiceSpecie],
    queryFn: ({ signal }) => resources.breeds.list(codiceSpecie, signal),
    enabled: tipo === 'registration' && codiceSpecie !== '',
  })

  const crea = useMutation({
    mutationFn: (corpo: Record<string, unknown>) => resources.declarations.create(corpo),
    onMutate: () => {
      setErrore(null)
      setViolazioni({})
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['declarations'] })
      onFatto()
    },
    onError: (e: unknown) => {
      const problema = e as {
        status?: number
        violations?: readonly { propertyPath: string; message: string }[]
      }

      if (problema.status === 422 && problema.violations) {
        setViolazioni(
          Object.fromEntries(problema.violations.map((v) => [v.propertyPath, v.message])),
        )
        setErrore('Alcuni dati non vanno bene: sono segnati qui sotto.')

        return
      }

      setErrore(
        problema.status === 403
          ? 'Il vostro profilo non può aprire denunce.'
          : 'Non è stato possibile aprire la denuncia.',
      )
    },
  })

  function invia(evento: SyntheticEvent<HTMLFormElement>) {
    evento.preventDefault()

    const campi = new FormData(evento.currentTarget)
    const testoDeiCampi = Object.fromEntries(
      [...campi.entries()].filter(([, v]) => typeof v === 'string'),
    ) as Record<string, string>
    const testo = (chiave: string) => (testoDeiCampi[chiave] ?? '').trim()

    // Il contenuto si compone per tipo, e le regole stanno in `core` con le
    // loro prove: quali chiavi appartengono a quale tipo, e il fatto che i
    // conteggi della nascita si ricavino dall'elenco invece di essere
    // digitati a parte.
    const contenuto = contenutoDenuncia(
      tipo,
      testoDeiCampi,
      { proprietarioId: proprietario?.id, stalloneId: stallone?.id },
      nati,
    )

    crea.mutate({
      species: codiceSpecie,
      type: tipo,
      declared_on: testo('declared_on') || oggi(),
      declarant: iri('people', denunciante?.id) ?? '',
      // Il soggetto è la fattrice per la monta e la nascita, il soggetto
      // trasferito per il passaggio; l'iscrizione non ne ha uno, perché il
      // soggetto è proprio quello che verrà creato.
      animal: iri('animals', soggetto?.id),
      payload: contenuto,
    })
  }

  const vuoleSoggetto = tipo === 'mating' || tipo === 'birth' || tipo === 'transfer'

  return (
    <section aria-labelledby="titolo-modulo-denuncia">
      <button type="button" data-variante="piatto" onClick={onAnnulla}>
        ← Annulla
      </button>

      <h1 id="titolo-modulo-denuncia">Nuova denuncia</h1>

      {errore && (
        <p role="alert" className="messaggio messaggio--errore">
          {errore}
        </p>
      )}

      <form className="modulo" onSubmit={invia}>
        <fieldset>
          <legend>Tipo</legend>
          <label className="modulo__campo">
            <span>Che cosa si denuncia</span>
            <select
              value={tipo}
              onChange={(evento) => {
                setTipo(evento.target.value as DeclarationType)
                // I riferimenti cambiano di significato col tipo: lo stallone
                // di una monta non è il soggetto di un passaggio. Tenerli
                // sarebbe peggio che perderli.
                setSoggetto(null)
                setStallone(null)
                setProprietario(null)
                setViolazioni({})
              }}
            >
              {TIPI.map((t) => (
                <option key={t} value={t}>
                  {typeLabel(t)}
                </option>
              ))}
            </select>
          </label>
          <Campo nome="declared_on" etichetta="Dichiarata il" tipo="date" predefinito={oggi()} violazioni={violazioni} />
        </fieldset>

        <fieldset>
          <legend>Chi dichiara</legend>
          <CercaPersona scelta={denunciante} onScegli={setDenunciante} etichetta="Denunciante" />
          {/*
            Il denunciante è l'unico dato che il modulo pretende prima di
            inviare. Non è una validazione doppia: senza, la richiesta non
            arriverebbe nemmeno alla validazione — il denunciante è un
            riferimento obbligatorio, e omesso produce un `400` che nomina la
            classe PHP e il parametro del costruttore. Meglio non partire.
          */}
          {!denunciante && (
            <p className="riga__secondario">
              Serve indicare chi presenta la denuncia prima di poterla aprire.
            </p>
          )}
          {violazioni['declarant'] && (
            <span className="modulo__errore" role="alert">
              {violazioni['declarant']}
            </span>
          )}
        </fieldset>

        {vuoleSoggetto && (
          <fieldset>
            <legend>{tipo === 'transfer' ? 'Soggetto trasferito' : parolaFattrice}</legend>
            <CercaSoggetto scelta={soggetto} onScegli={setSoggetto} />
          </fieldset>
        )}

        {tipo === 'registration' && (
          <fieldset>
            <legend>Soggetto da iscrivere</legend>
            <Campo nome="name" etichetta="Nome" violazioni={violazioni} obbligatorio />
            <label className="modulo__campo">
              <span>Sesso</span>
              <select name="sex" defaultValue="U">
                <option value="M">Maschio</option>
                <option value="F">Femmina</option>
                <option value="U">Non determinato</option>
              </select>
            </label>
            <Campo nome="birth_date" etichetta="Data di nascita" tipo="date" violazioni={violazioni} />
            <Campo nome="color" etichetta="Colore" violazioni={violazioni} />
            <label className="modulo__campo">
              <span>Razza</span>
              <select name="breed_id" defaultValue="">
                <option value="">— nessuna —</option>
                {razze.data?.member.map((razza) => (
                  <option key={razza.id} value={razza.id}>
                    {razza.name}
                  </option>
                ))}
              </select>
            </label>
          </fieldset>
        )}

        {tipo === 'mating' && (
          <fieldset>
            <legend>Monta</legend>
            <CercaSoggetto scelta={stallone} onScegli={setStallone} sesso="M" etichetta="Stallone" />
            <Campo nome="mating_date" etichetta="Data della monta" tipo="date" violazioni={violazioni} />
          </fieldset>
        )}

        {tipo === 'birth' && (
          <fieldset>
            <legend>Nascita</legend>
            <Campo nome="birth_date" etichetta="Data del parto" tipo="date" violazioni={violazioni} />
            <ElencoNati nati={nati} onCambia={setNati} parola={parolaNato} />
          </fieldset>
        )}

        {(tipo === 'registration' || tipo === 'transfer') && (
          <fieldset>
            <legend>{tipo === 'transfer' ? 'Nuovo proprietario' : 'Proprietario'}</legend>
            <CercaPersona
              scelta={proprietario}
              onScegli={setProprietario}
              etichetta={tipo === 'transfer' ? 'Nuovo proprietario' : 'Proprietario'}
            />
            {tipo === 'transfer' && (
              <Campo nome="date" etichetta="Data del passaggio" tipo="date" violazioni={violazioni} />
            )}
          </fieldset>
        )}

        <fieldset>
          <legend>Note</legend>
          <label className="modulo__campo modulo__campo--largo">
            <span>Note</span>
            <textarea name="notes" rows={3} />
          </label>
        </fieldset>

        {/*
          La denuncia nasce in bozza, e il pulsante lo dice. Chi la apre deve
          sapere che non è ancora stata presentata: il contenuto si congela
          alla presentazione, e prima di allora si può ancora correggere.
        */}
        <div className="modulo__azioni">
          <button type="button" data-variante="piatto" onClick={onAnnulla} disabled={crea.isPending}>
            Annulla
          </button>
          <button type="submit" disabled={crea.isPending || !denunciante}>
            {crea.isPending ? 'Apertura…' : 'Apri in bozza'}
          </button>
        </div>
      </form>
    </section>
  )
}

/**
 * I nati, uno per riga.
 *
 * I conteggi (totale, maschi, femmine) **non** si digitano: si ricavano da
 * questo elenco. Chiederli a parte significherebbe avere due dati che devono
 * coincidere e che, quando non coincidono, non dicono quale dei due sia
 * giusto — e il registro verrebbe costruito sull'elenco, non sul conteggio.
 */
function ElencoNati({
  nati,
  onCambia,
  parola,
}: {
  nati: readonly Nato[]
  onCambia: (nati: readonly Nato[]) => void
  parola: string
}) {
  function aggiorna(indice: number, campo: keyof Nato, valore: string) {
    onCambia(nati.map((nato, i) => (i === indice ? { ...nato, [campo]: valore } : nato)))
  }

  const contati = nati.filter((n) => n.name.trim() !== '')

  return (
    <div className="nati">
      {nati.map((nato, indice) => (
        <div className="nati__riga" key={indice}>
          <input
            type="text"
            value={nato.name}
            placeholder={`Nome del ${parola.toLowerCase()}`}
            onChange={(evento) => {
              aggiorna(indice, 'name', evento.target.value)
            }}
          />
          <select
            value={nato.sex}
            onChange={(evento) => {
              aggiorna(indice, 'sex', evento.target.value)
            }}
          >
            <option value="M">Maschio</option>
            <option value="F">Femmina</option>
            <option value="U">Non determinato</option>
          </select>
          <input
            type="text"
            value={nato.color}
            placeholder="Colore"
            onChange={(evento) => {
              aggiorna(indice, 'color', evento.target.value)
            }}
          />
          <button
            type="button"
            data-variante="piatto"
            aria-label={`Togli la riga ${String(indice + 1)}`}
            onClick={() => {
              onCambia(nati.filter((_, i) => i !== indice))
            }}
          >
            ×
          </button>
        </div>
      ))}

      <button
        type="button"
        data-variante="secondario"
        onClick={() => {
          onCambia([...nati, { name: '', sex: 'U', color: '' }])
        }}
      >
        Aggiungi una riga
      </button>

      <p className="riga__secondario" aria-live="polite">
        {contati.length === 0
          ? 'Nessun nato indicato: senza almeno un nome la registrazione non potrà procedere.'
          : `${String(contati.length)} nati · ${String(contati.filter((n) => n.sex === 'M').length)} maschi · ${String(contati.filter((n) => n.sex === 'F').length)} femmine`}
      </p>
    </div>
  )
}

function CercaPersona({
  scelta,
  onScegli,
  etichetta,
}: {
  scelta: PersonRead | null
  onScegli: (persona: PersonRead | null) => void
  etichetta: string
}) {
  const { resources } = useSession()
  const [testo, setTesto] = useState('')

  const risultati = useQuery({
    queryKey: ['people', 'cerca', testo],
    queryFn: ({ signal }) =>
      resources.people.list({ last_name: testo.trim(), items_per_page: 8 }, signal),
    enabled: testo.trim().length >= 2,
  })

  if (scelta) {
    return (
      <div className="scelta">
        <span className="riga__nome">{scelta.display_name}</span>
        <button
          type="button"
          data-variante="piatto"
          onClick={() => {
            onScegli(null)
            setTesto('')
          }}
        >
          Cambia
        </button>
      </div>
    )
  }

  return (
    <div>
      <label className="modulo__campo">
        <span>{etichetta} — cerca per cognome</span>
        <input
          type="search"
          value={testo}
          onChange={(evento) => {
            setTesto(evento.target.value)
          }}
        />
      </label>

      {testo.trim().length >= 2 && risultati.data?.member.length === 0 && (
        <p className="modulo__errore">Nessuna persona trovata.</p>
      )}

      {risultati.data && risultati.data.member.length > 0 && (
        <ul className="scelta__elenco">
          {risultati.data.member.map((persona) => (
            <li key={persona.id}>
              <button
                type="button"
                data-variante="piatto"
                onClick={() => {
                  onScegli(persona)
                }}
              >
                {persona.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * La ricerca di un soggetto, ristretta alla specie in uso.
 *
 * Il filtro sul sesso c'è dove il ruolo lo impone — lo stallone di una monta —
 * ma **non sostituisce il controllo del server**: filtrare l'elenco evita di
 * scegliere male, non garantisce che la scelta sia valida, e la regola vera
 * («lo stallone indicato non è di sesso maschile») resta dov'era.
 */
function CercaSoggetto({
  scelta,
  onScegli,
  sesso,
  etichetta = 'Soggetto',
}: {
  scelta: AnimalRead | null
  onScegli: (animale: AnimalRead | null) => void
  sesso?: 'M' | 'F'
  etichetta?: string
}) {
  const { resources, species } = useSession()
  const [testo, setTesto] = useState('')

  const risultati = useQuery({
    queryKey: ['animals', 'cerca', species?.code, sesso, testo],
    queryFn: ({ signal }) => {
      const filtri: AnimalQuery = { q: testo.trim(), items_per_page: 8 }

      if (species) filtri['species.code'] = species.code
      if (sesso) filtri.sex = sesso

      return resources.animals.list(filtri, signal)
    },
    enabled: testo.trim().length >= 2,
  })

  if (scelta) {
    return (
      <div className="scelta">
        <span className="riga__nome">{scelta.full_name ?? scelta.name}</span>
        {scelta.registry_number && <span className="codice">{scelta.registry_number}</span>}
        <button
          type="button"
          data-variante="piatto"
          onClick={() => {
            onScegli(null)
            setTesto('')
          }}
        >
          Cambia
        </button>
      </div>
    )
  }

  return (
    <div>
      <label className="modulo__campo">
        <span>{etichetta} — cerca per nome, numero di registro o microchip</span>
        <input
          type="search"
          value={testo}
          onChange={(evento) => {
            setTesto(evento.target.value)
          }}
        />
      </label>

      {testo.trim().length >= 2 && risultati.data?.member.length === 0 && (
        <p className="modulo__errore">Nessun soggetto trovato.</p>
      )}

      {risultati.data && risultati.data.member.length > 0 && (
        <ul className="scelta__elenco">
          {risultati.data.member.map((animale) => (
            <li key={animale.id}>
              <button
                type="button"
                data-variante="piatto"
                onClick={() => {
                  onScegli(animale)
                }}
              >
                {animale.full_name ?? animale.name}
                {animale.registry_number && (
                  <span className="riga__secondario"> · {animale.registry_number}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Campo({
  nome,
  etichetta,
  violazioni,
  tipo = 'text',
  predefinito,
  obbligatorio = false,
}: {
  nome: string
  etichetta: string
  violazioni: Record<string, string>
  tipo?: string
  predefinito?: string | undefined
  obbligatorio?: boolean
}) {
  const errore = violazioni[nome]
  const idErrore = `errore-${nome}`

  return (
    <label className="modulo__campo">
      <span>
        {etichetta}
        {obbligatorio && <abbr title="obbligatorio"> *</abbr>}
      </span>
      <input
        type={tipo}
        name={nome}
        defaultValue={predefinito ?? ''}
        aria-invalid={errore ? true : undefined}
        aria-describedby={errore ? idErrore : undefined}
      />
      {errore && (
        <span className="modulo__errore" id={idErrore} role="alert">
          {errore}
        </span>
      )}
    </label>
  )
}

/** Oggi in `AAAA-MM-GG`, che è la forma che accettano i campi data. */
function oggi(): string {
  return new Date().toISOString().slice(0, 10)
}
